/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {
  HomePagePullRequestFragment,
  AuthoredLoadMoreQueryData,
  AuthoredLoadMoreQueryVariables,
  ReviewedByMeLoadMoreQueryData,
  ReviewedByMeLoadMoreQueryVariables,
} from './generated/graphql';

import CenteredSpinner from './CenteredSpinner';
import Link from './Link';
import PullRequestStateLabel from './PullRequestStateLabel';
import TrustedRenderedMarkdown from './TrustedRenderedMarkdown';
import queryGraphQL from './github/queryGraphQL';
import {createGraphQLEndpointForHostname} from './github/gitHubCredentials';
import {createRequestHeaders} from 'shared/github/auth';
import {gitHubUserHomePageDataAtom, gitHubUsernameAtom, includeDraftPRsAtom} from './jotai/atoms';
import {
  PullRequestReviewDecision,
  PullRequestState,
  AuthoredLoadMoreQuery,
  ReviewedByMeLoadMoreQuery,
} from './generated/graphql';
import {GearIcon} from '@primer/octicons-react';
import {ActionList, ActionMenu, Box, IconButton, Text} from '@primer/react';
import {useAtom, useAtomValue} from 'jotai';
import {Suspense, useCallback, useMemo, useRef, useState} from 'react';
import {notEmpty} from 'shared/utils';

type SectionId = 'action-required' | 'authored' | 'reviewed';

const SEEN_STORAGE_KEY = 'reviewstack.seenPRs';

/** Read the map of PR key -> last-seen timestamp from localStorage. */
function getSeenMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

/** Mark a PR as seen right now, persisting to localStorage. Returns the updated map. */
function markPRSeen(key: string): Record<string, number> {
  const map = getSeenMap();
  map[key] = Date.now();
  // Prune entries older than 90 days to avoid unbounded growth
  const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
  for (const k of Object.keys(map)) {
    if (map[k] < cutoff) {
      delete map[k];
    }
  }
  localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(map));
  return map;
}

interface SectionDef {
  id: SectionId;
  label: string;
  items: AnnotatedPR[];
  showStatus: boolean;
  totalCount?: number;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

/** PR with computed activity flags. */
interface AnnotatedPR {
  pr: HomePagePullRequestFragment;
  /** True if the PR was promoted via unseen activity (shows "Updated" badge always). */
  updated: boolean;
  /** True if the user hasn't clicked through yet (shows blue dot, dismissable). */
  unseen: boolean;
}

export default function UserHomePage(): React.ReactElement {
  return (
    <Suspense fallback={<CenteredSpinner />}>
      <UserHomePageRoot />
    </Suspense>
  );
}

function prKey(pr: HomePagePullRequestFragment): string {
  return `${pr.repository.nameWithOwner}#${pr.number}`;
}

function extractPRs(
  nodes: ReadonlyArray<{__typename?: string} | null | undefined> | null | undefined,
): HomePagePullRequestFragment[] {
  return (nodes ?? [])
    .map(node => (node?.__typename === 'PullRequest' ? (node as HomePagePullRequestFragment) : null))
    .filter(notEmpty);
}

/**
 * Format a relative time string like "5m", "2h", "3d".
 */
function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) {
    return 'now';
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days}d`;
  }
  const months = Math.floor(days / 30);
  return `${months}mo`;
}

/** Sort PRs by updatedAt descending. */
function sortByUpdated(prs: HomePagePullRequestFragment[]): HomePagePullRequestFragment[] {
  return [...prs].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

/**
 * Determine if a PR should appear in "Needs your review" based on two rules:
 *
 * Rule 1: The PR is open and not in a terminal review state (approved or changes-requested).
 * Rule 2: The PR has changes-requested, but the author has responded (commented or pushed)
 *         since the last changes-requested review.
 */
function shouldShowInNeedsReview(pr: HomePagePullRequestFragment): boolean {
  if (pr.state !== PullRequestState.Open) {
    return false;
  }

  const decision = pr.reviewDecision;

  // Rule 1: Not in a terminal review state → needs review
  if (decision !== PullRequestReviewDecision.Approved &&
      decision !== PullRequestReviewDecision.ChangesRequested) {
    return true;
  }

  // Rule 2: Changes requested but author has responded
  if (decision === PullRequestReviewDecision.ChangesRequested) {
    return hasAuthorRespondedSinceChangesRequested(pr);
  }

  return false;
}

/**
 * Check if the PR author has commented or pushed since the last
 * changes-requested review was submitted.
 */
function hasAuthorRespondedSinceChangesRequested(pr: HomePagePullRequestFragment): boolean {
  const changesReview = pr.latestChangesRequested?.nodes?.[0];
  if (!changesReview?.submittedAt) {
    return false;
  }
  const changesAt = new Date(changesReview.submittedAt).getTime();
  const authorLogin = pr.author?.login;

  // New push since changes were requested?
  const lastCommit = pr.latestCommit.nodes?.[0]?.commit;
  if (lastCommit && new Date(lastCommit.committedDate).getTime() > changesAt) {
    return true;
  }

  // Author commented since changes were requested?
  const comments = pr.latestNonViewerComment.nodes ?? [];
  for (const comment of comments) {
    if (comment != null &&
        comment.author?.login === authorLogin &&
        new Date(comment.createdAt).getTime() > changesAt) {
      return true;
    }
  }

  return false;
}

function UserHomePageRoot(): React.ReactElement {
  const data = useAtomValue(gitHubUserHomePageDataAtom);
  const viewerLogin = useAtomValue(gitHubUsernameAtom);
  const [includeDrafts, setIncludeDrafts] = useAtom(includeDraftPRsAtom);
  const [collapsed, setCollapsed] = useState<Set<SectionId>>(new Set());
  const [activeNav, setActiveNav] = useState<SectionId>('action-required');
  const [seenMap, setSeenMap] = useState<Record<string, number>>(getSeenMap);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggle = useCallback((id: SectionId) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const scrollToSection = useCallback((id: SectionId) => {
    setActiveNav(id);
    setCollapsed(prev => {
      if (prev.has(id)) {
        const next = new Set(prev);
        next.delete(id);
        return next;
      }
      return prev;
    });
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }, []);

  // Pagination state for all three sections
  const [extraAuthored, setExtraAuthored] = useState<HomePagePullRequestFragment[]>([]);
  const [authoredCursor, setAuthoredCursor] = useState<string | null>(null);
  const [authoredHasMore, setAuthoredHasMore] = useState<boolean>(false);
  const [loadingMoreAuthored, setLoadingMoreAuthored] = useState(false);

  const [extraReviewed, setExtraReviewed] = useState<HomePagePullRequestFragment[]>([]);
  const [reviewedCursor, setReviewedCursor] = useState<string | null>(null);
  const [reviewedHasMore, setReviewedHasMore] = useState<boolean>(false);
  const [loadingMoreReviewed, setLoadingMoreReviewed] = useState(false);

  const cursorInitialized = useRef(false);

  const reviewRequested = extractPRs(data?.reviewRequested.nodes);
  const reviewedNeedsAction = extractPRs(data?.reviewedNeedsAction.nodes);
  const reviewedByMeInitial = extractPRs(data?.reviewedByMe.nodes);
  const myPullRequests = [...(data?.viewer.pullRequests.nodes ?? []).filter(notEmpty), ...extraAuthored];
  const repos = (data?.viewer.repositories.nodes ?? []).filter(notEmpty);

  // Initialize cursors from initial query (once)
  if (!cursorInitialized.current && data) {
    cursorInitialized.current = true;
    if (data.viewer.pullRequests.pageInfo) {
      setAuthoredCursor(data.viewer.pullRequests.pageInfo.endCursor ?? null);
      setAuthoredHasMore(data.viewer.pullRequests.pageInfo.hasNextPage);
    }
    if (data.reviewedByMe.pageInfo) {
      setReviewedCursor(data.reviewedByMe.pageInfo.endCursor ?? null);
      setReviewedHasMore(data.reviewedByMe.pageInfo.hasNextPage);
    }
  }

  const authoredKeys = new Set(myPullRequests.map(prKey));
  const allReviewed = [...reviewedByMeInitial, ...extraReviewed];
  const authoredTotalCount = data?.viewer.pullRequests.totalCount ?? myPullRequests.length;
  const reviewedTotalCount = data?.reviewedByMe.issueCount ?? allReviewed.length;

  // Compute sections with filtering per review rules
  const sections: SectionDef[] = useMemo(() => {
    // "Needs your review" — deduplicated union of reviewRequested + reviewedNeedsAction
    const seenKeys = new Set<string>();
    const allNeedsReview: HomePagePullRequestFragment[] = [];
    for (const pr of [...reviewRequested, ...reviewedNeedsAction]) {
      const key = prKey(pr);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        allNeedsReview.push(pr);
      }
    }

    // "Needs your review" — PRs from dedicated queries, filtered by rules
    const actionItems = sortByUpdated(
      allNeedsReview.filter(pr =>
        !authoredKeys.has(prKey(pr)) &&
        shouldShowInNeedsReview(pr) &&
        !(!includeDrafts && pr.isDraft),
      ),
    );
    const actionKeys = new Set(actionItems.map(prKey));

    // Annotate: PRs that are changes-requested but author responded get "Updated" badge
    const annotatedAction: AnnotatedPR[] = actionItems.map(pr => {
      const key = prKey(pr);
      const isChangesResponded =
        pr.reviewDecision === PullRequestReviewDecision.ChangesRequested;
      const seenAt = seenMap[key];
      const manuallyViewed = seenAt != null && seenAt >= new Date(pr.updatedAt).getTime();
      return {pr, updated: isChangesResponded, unseen: isChangesResponded && !manuallyViewed};
    });

    // "Your revisions" sorted by updated
    const authored: AnnotatedPR[] = sortByUpdated(myPullRequests).map(pr => ({pr, updated: false, unseen: false}));

    // "Reviewed by you" — exclude authored and action-required, sorted by updated
    const reviewed: AnnotatedPR[] = sortByUpdated(
      allReviewed.filter(pr => !authoredKeys.has(prKey(pr)) && !actionKeys.has(prKey(pr))),
    ).map(pr => ({pr, updated: false, unseen: false}));

    return [
      {
        id: 'action-required' as SectionId,
        label: 'Needs your review',
        items: annotatedAction,
        showStatus: true,
        // No totalCount — this section is a filtered composite of reviewRequested + reviewedByMe,
        // so no single API count represents it accurately.
      },
      {
        id: 'authored' as SectionId,
        label: 'Your revisions',
        items: authored,
        showStatus: true,
        totalCount: authoredTotalCount,
        hasMore: authoredHasMore,
      },
      {
        id: 'reviewed' as SectionId,
        label: 'Reviewed by you',
        items: reviewed,
        showStatus: true,
        totalCount: reviewedTotalCount,
        hasMore: reviewedHasMore,
      },
    ];
  }, [reviewRequested, reviewedNeedsAction, allReviewed, myPullRequests, authoredKeys,
      authoredTotalCount, authoredHasMore,
      reviewedTotalCount, reviewedHasMore, seenMap, includeDrafts]);

  const getGraphQLContext = useCallback(() => {
    const token = localStorage.getItem('github.token');
    if (!token) {
      return null;
    }
    const hostname = localStorage.getItem('github.hostname') ?? 'github.com';
    const graphQLEndpoint = createGraphQLEndpointForHostname(hostname);
    const headers = createRequestHeaders(token);
    return {graphQLEndpoint, headers};
  }, []);

  const loadMoreAuthored = useCallback(async () => {
    if (loadingMoreAuthored || !authoredCursor || !viewerLogin) {
      return;
    }
    setLoadingMoreAuthored(true);
    try {
      const ctx = getGraphQLContext();
      if (!ctx) return;
      const result = await queryGraphQL<AuthoredLoadMoreQueryData, AuthoredLoadMoreQueryVariables>(
        AuthoredLoadMoreQuery,
        {after: authoredCursor, viewerLogin},
        ctx.headers,
        ctx.graphQLEndpoint,
      );
      const newPRs = (result.viewer.pullRequests.nodes ?? []).filter(notEmpty);
      setExtraAuthored(prev => [...prev, ...newPRs]);
      setAuthoredCursor(result.viewer.pullRequests.pageInfo.endCursor ?? null);
      setAuthoredHasMore(result.viewer.pullRequests.pageInfo.hasNextPage);
    } finally {
      setLoadingMoreAuthored(false);
    }
  }, [loadingMoreAuthored, authoredCursor, viewerLogin, getGraphQLContext]);

  const loadMoreReviewed = useCallback(async () => {
    if (loadingMoreReviewed || !reviewedCursor || !viewerLogin) {
      return;
    }
    setLoadingMoreReviewed(true);
    try {
      const ctx = getGraphQLContext();
      if (!ctx) return;
      const reviewedByMeQuery = 'is:pr archived:false reviewed-by:@me sort:updated-desc';
      const result = await queryGraphQL<ReviewedByMeLoadMoreQueryData, ReviewedByMeLoadMoreQueryVariables>(
        ReviewedByMeLoadMoreQuery,
        {query: reviewedByMeQuery, after: reviewedCursor, viewerLogin},
        ctx.headers,
        ctx.graphQLEndpoint,
      );
      const newPRs = extractPRs(result.search.nodes);
      setExtraReviewed(prev => [...prev, ...newPRs]);
      setReviewedCursor(result.search.pageInfo.endCursor ?? null);
      setReviewedHasMore(result.search.pageInfo.hasNextPage);
    } finally {
      setLoadingMoreReviewed(false);
    }
  }, [loadingMoreReviewed, reviewedCursor, viewerLogin, getGraphQLContext]);

  // Attach load-more handlers to each section
  const sectionsWithHandlers = sections.map(s => {
    if (s.id === 'authored') {
      return {...s, onLoadMore: loadMoreAuthored, loadingMore: loadingMoreAuthored};
    }
    if (s.id === 'reviewed') {
      return {...s, onLoadMore: loadMoreReviewed, loadingMore: loadingMoreReviewed};
    }
    return s;
  });

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 48px)',
        overflow: 'hidden',
      }}>
      {/* Sidebar — fixed, scrolls independently */}
      <Box
        sx={{
          width: '230px',
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'border.default',
          bg: 'canvas.default',
          pt: 3,
          pb: 2,
          display: ['none', 'none', 'flex'],
          flexDirection: 'column',
          overflowY: 'auto',
        }}>
        {/* Section nav */}
        <Box sx={{px: '10px'}}>
          {sectionsWithHandlers.map(section => {
            const isActive = activeNav === section.id;
            return (
              <Box
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '12px',
                  py: '8px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  bg: isActive ? 'neutral.muted' : 'transparent',
                  ':hover': {bg: isActive ? 'neutral.muted' : 'canvas.subtle'},
                }}>
                <Text
                  sx={{
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'fg.default' : 'fg.muted',
                  }}>
                  {section.label}
                </Text>
                <Text
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'fg.muted',
                    minWidth: '24px',
                    textAlign: 'right',
                  }}>
                  {section.totalCount ?? section.items.length}
                </Text>
              </Box>
            );
          })}
        </Box>

        {/* Sidebar divider */}
        <Box sx={{borderBottom: '1px solid', borderColor: 'border.muted', mx: '10px', my: '12px'}} />

        {/* Repositories in sidebar */}
        {repos.length > 0 && (
          <Box sx={{px: '10px', flex: 1, overflowY: 'auto'}}>
            <Text
              sx={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 600,
                color: 'fg.muted',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                px: '12px',
                mb: '6px',
              }}>
              Repositories
            </Text>
            {repos.map(repo => (
              <Link key={repo.nameWithOwner} href={`/${repo.nameWithOwner}/pulls`}>
                <Box
                  sx={{
                    px: '12px',
                    py: '4px',
                    fontSize: '12px',
                    color: 'accent.fg',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    ':hover': {bg: 'canvas.subtle'},
                  }}>
                  {repo.nameWithOwner}
                </Box>
              </Link>
            ))}
          </Box>
        )}
      </Box>

      {/* Main content — scrolls independently */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflowY: 'auto',
          py: '20px',
          px: [2, 3, '24px'],
          bg: 'canvas.subtle',
        }}>
        <Box sx={{maxWidth: '1100px', display: 'flex', flexDirection: 'column', gap: '20px'}}>
          {sectionsWithHandlers.map(section => {
            const isCollapsed = collapsed.has(section.id);
            return (
              <Box
                key={section.id}
                ref={(el: HTMLDivElement | null) => {
                  sectionRefs.current[section.id] = el;
                }}
                sx={{
                  border: '1px solid',
                  borderColor: 'border.default',
                  borderRadius: '12px',
                  bg: 'canvas.default',
                  boxShadow: 'shadow.small',
                }}>
                {/* Section header — sticky within scroll container */}
                <Box
                  onClick={() => toggle(section.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    px: '16px',
                    py: '10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderBottom: isCollapsed ? 'none' : '1px solid',
                    borderColor: 'border.muted',
                    bg: 'canvas.default',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    borderRadius: isCollapsed ? '12px' : '12px 12px 0 0',
                    ':hover': {bg: 'canvas.subtle'},
                  }}>
                  <Text
                    sx={{
                      fontSize: '12px',
                      color: 'fg.muted',
                      width: '14px',
                      textAlign: 'center',
                      transition: 'transform 0.15s ease',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    }}>
                    {'\u25BE'}
                  </Text>
                  <Text sx={{fontSize: '15px', fontWeight: 600, color: 'fg.default', flex: 1}}>
                    {section.label}
                    <Text sx={{fontWeight: 400, color: 'fg.muted', ml: '8px'}}>
                      {section.totalCount ?? section.items.length}
                      {section.totalCount != null && section.items.length < section.totalCount
                        ? ` (${section.items.length})`
                        : null}
                    </Text>
                  </Text>
                  {section.id === 'action-required' && (
                    <Box
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      sx={{flexShrink: 0}}>
                      <ActionMenu>
                        <ActionMenu.Anchor>
                          <IconButton
                            icon={GearIcon}
                            aria-label="Review settings"
                            size="small"
                            variant="invisible"
                            sx={{color: 'fg.muted'}}
                          />
                        </ActionMenu.Anchor>
                        <ActionMenu.Overlay>
                          <ActionList>
                            <ActionList.Item onSelect={() => setIncludeDrafts(prev => !prev)}>
                              {includeDrafts && (
                                <ActionList.LeadingVisual>
                                  <CheckIcon />
                                </ActionList.LeadingVisual>
                              )}
                              Include draft PRs
                            </ActionList.Item>
                          </ActionList>
                        </ActionMenu.Overlay>
                      </ActionMenu>
                    </Box>
                  )}
                </Box>

                {/* Column header */}
                {!isCollapsed && section.items.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: '16px',
                      py: '4px',
                      borderBottom: '1px solid',
                      borderColor: 'border.muted',
                      bg: 'canvas.subtle',
                    }}>
                    {/* Status column */}
                    {section.showStatus && (
                      <Box sx={{width: '120px', flexShrink: 0}} />
                    )}
                    <Text sx={{flex: 1, fontSize: '11px', fontWeight: 600, color: 'fg.muted', textTransform: 'uppercase', letterSpacing: '0.3px'}}>
                      Title
                    </Text>
                    <Text sx={{width: '90px', flexShrink: 0, fontSize: '11px', fontWeight: 600, color: 'fg.muted', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'right', display: ['none', 'none', 'block']}}>
                      Changes
                    </Text>
                    <Text sx={{width: '70px', flexShrink: 0, fontSize: '11px', fontWeight: 600, color: 'fg.muted', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'right', display: ['none', 'none', 'block']}}>
                      Updated
                    </Text>
                  </Box>
                )}

                {/* Section rows */}
                {!isCollapsed && (
                  <Box>
                    {section.items.length === 0 ? (
                      <Box sx={{px: '16px', py: '24px', textAlign: 'center'}}>
                        <Text sx={{color: 'fg.muted', fontSize: '13px'}}>
                          Nothing here.
                        </Text>
                      </Box>
                    ) : (
                      <>
                        {section.items.map(({pr, unseen, updated}, index) => (
                          <RevisionRow
                            key={prKey(pr)}
                            pr={pr}
                            showStatus={section.showStatus}
                            unseen={unseen}
                            updated={updated}
                            isLast={!section.hasMore && index === section.items.length - 1}
                            onMarkSeen={() => setSeenMap(markPRSeen(prKey(pr)))}
                          />
                        ))}
                        {section.hasMore && (
                          <Box
                            onClick={section.onLoadMore}
                            sx={{
                              px: '16px',
                              py: '10px',
                              textAlign: 'center',
                              cursor: section.loadingMore ? 'default' : 'pointer',
                              borderTop: '1px solid',
                              borderColor: 'border.muted',
                              ':hover': {bg: section.loadingMore ? 'transparent' : 'canvas.subtle'},
                            }}>
                            <Text sx={{fontSize: '13px', fontWeight: 500, color: 'accent.fg'}}>
                              {section.loadingMore
                                ? 'Loading...'
                                : `Show more (${section.items.length} of ${section.totalCount ?? '?'})`}
                            </Text>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Revision row
// ---------------------------------------------------------------------------

function RevisionRow({
  pr,
  showStatus,
  unseen,
  updated,
  isLast,
  onMarkSeen,
}: {
  pr: HomePagePullRequestFragment;
  showStatus: boolean;
  /** Blue dot — dismissable on click. */
  unseen: boolean;
  /** "Updated" badge — persistent, indicates changes since last review. */
  updated: boolean;
  isLast: boolean;
  onMarkSeen: () => void;
}) {
  const {number, title, titleHTML, repository, state, isDraft, reviewDecision, updatedAt, additions, deletions, author, lastComment} = pr;
  const {nameWithOwner} = repository;
  const lastCommentBody = lastComment.nodes?.[0]?.body ?? null;

  return (
    <Link href={`/${nameWithOwner}/pull/${number}`}>
      <Box
        onClick={onMarkSeen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: '16px',
          py: '10px',
          borderBottom: isLast ? 'none' : '1px solid',
          borderColor: 'border.muted',
          cursor: 'pointer',
          ':hover': {bg: 'canvas.subtle'},
        }}>
        {/* Unseen indicator dot */}
        {unseen && (
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              bg: 'accent.fg',
              flexShrink: 0,
              mr: '10px',
            }}
          />
        )}

        {/* Status badge — fixed width column for alignment */}
        {showStatus && (
          <Box sx={{width: '120px', flexShrink: 0}}>
            <PullRequestStateLabel reviewDecision={reviewDecision ?? null} state={state} isDraft={isDraft} lastCommentBody={lastCommentBody} />
          </Box>
        )}

        {/* Title + subtitle */}
        <Box sx={{flex: 1, minWidth: 0}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <Text
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'fg.default',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {titleHTML ? (
                <TrustedRenderedMarkdown trustedHTML={titleHTML} inline={true} />
              ) : (
                title
              )}
            </Text>
            {updated && (
              <Text
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#0969da',
                  bg: '#0969da14',
                  border: '1px solid',
                  borderColor: '#0969da40',
                  borderRadius: '4px',
                  px: '6px',
                  py: '1px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}>
                Updated
              </Text>
            )}
          </Box>
          <Text
            sx={{
              fontSize: '12px',
              color: 'fg.muted',
              display: 'block',
              mt: '2px',
            }}>
            {author?.login ?? 'unknown'}
            <Text sx={{mx: '5px', color: 'border.default'}}>{'\u00B7'}</Text>
            {nameWithOwner}
            <Text sx={{mx: '5px', color: 'border.default'}}>{'\u00B7'}</Text>
            <Text sx={{color: 'accent.fg', fontWeight: 500}}>D{number}</Text>
          </Text>
        </Box>

        {/* Changes column */}
        <Box
          sx={{
            width: '90px',
            flexShrink: 0,
            textAlign: 'right',
            fontSize: '12px',
            fontFamily: 'mono',
            display: ['none', 'none', 'block'],
          }}>
          <Text sx={{color: '#cf222e'}}>-{deletions}</Text>
          <Text sx={{color: 'fg.muted', mx: '2px'}}>/</Text>
          <Text sx={{color: '#1a7f37'}}>+{additions}</Text>
        </Box>

        {/* Updated column */}
        <Text
          sx={{
            width: '70px',
            flexShrink: 0,
            textAlign: 'right',
            fontSize: '12px',
            color: 'fg.muted',
            display: ['none', 'none', 'block'],
          }}>
          {relativeTime(updatedAt)}
        </Text>
      </Box>
    </Link>
  );
}

/** Simple check mark icon matching GitHub's style. */
function CheckIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0Z" />
    </svg>
  );
}
